import React, { useEffect, useState } from 'react';
import './App.css';
import SplashScreen from './SplashScreen';
import LoginPage from './LoginPage';
import HomePage from './HomePage';
import { apiService, Member } from './services/api';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import RankDetails from './RankDetails';
import Achievements from './Achievements';
import Attendance from './Attendance';
import FeesDetails from './FeesDetails';
import Profile from './Profile';
import { testCacheFunctionality, testStudentSwitching, clearAllCache } from './utils/cacheTest';
import { validatePerformanceOptimizations, startPerformanceMonitoring, quickValidation } from './utils/validationScript';

// Make cache testing functions globally available for browser console
if (typeof window !== 'undefined') {
  (window as any).testCacheFunctionality = testCacheFunctionality;
  (window as any).testStudentSwitching = testStudentSwitching;
  (window as any).clearAllCache = clearAllCache;
  (window as any).apiService = apiService;
  (window as any).validatePerformanceOptimizations = validatePerformanceOptimizations;
  (window as any).startPerformanceMonitoring = startPerformanceMonitoring;
  (window as any).quickValidation = quickValidation;

  // Helper function to show available commands
  (window as any).showTestCommands = () => {
    console.log('🧪 Available Testing Commands:');
    console.log('===============================');
    console.log('testCacheFunctionality() - Test basic caching');
    console.log('testStudentSwitching() - Test student switching cache');
    console.log('clearAllCache() - Clear all cached data');
    console.log('apiService.getCacheStatus() - Show cache status');
    console.log('validatePerformanceOptimizations() - Full performance validation');
    console.log('startPerformanceMonitoring() - Start real-time monitoring');
    console.log('quickValidation() - Quick performance check');
    console.log('showTestCommands() - Show this help');
  };
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [students, setStudents] = useState<Member[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Member | null>(null);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [studentError, setStudentError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Check if user is already authenticated
    if (apiService.isAuthenticated()) {
      setIsAuthenticated(true);
      setUser({
        id: 1,
        name: 'User',
        mobile: '9898238919',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setLoadingStudents(true);
      setStudentError(null);
      apiService.getMembers()
        .then(response => {
          if (response.success && response.members.length > 0) {
            setStudents(response.members);
            setSelectedStudent(response.members[0]);
            // console.log('Fetched students:', response.members);
            // console.log('Selected student after fetch:', response.members[0]);
          } else {
            setStudentError('No students found');
          }
        })
        .catch(() => setStudentError('Failed to load students'))
        .finally(() => setLoadingStudents(false));
    }
  }, [isAuthenticated]);

  const handleLoginSuccess = (userData: any) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
  };

  const handleStudentChange = (student: any) => {
    // console.log('Student changed to:', student);
    setSelectedStudent(student);
  };

  if (showSplash) {
    return <SplashScreen isDarkTheme={false} />;
  }

  if (isAuthenticated && (loadingStudents || !selectedStudent || students.length === 0)) {
    return <div className="app light-theme"><div className="loading-container">Loading students...</div></div>;
  }
  if (isAuthenticated && studentError) {
    return <div className="app light-theme"><div className="error-container">{studentError}</div></div>;
  }

  // Helper renderers for each page to avoid TS prop mismatch errors
  const renderHomePage = () => (
    <HomePage onLogout={handleLogout} students={students} selectedStudent={selectedStudent} onStudentChange={handleStudentChange} />
  );
  const renderRankDetails = () => (
    <RankDetails students={students} selectedStudent={selectedStudent} onStudentChange={handleStudentChange} />
  );
  const renderAchievements = () => (
    <Achievements students={students} selectedStudent={selectedStudent} onStudentChange={handleStudentChange} />
  );
  const renderAttendance = () => (
    <Attendance students={students} selectedStudent={selectedStudent} onStudentChange={handleStudentChange} />
  );
  const renderFeesDetails = () => (
    <FeesDetails students={students} selectedStudent={selectedStudent} onStudentChange={handleStudentChange} />
  );
  const renderProfile = () => (
    <Profile students={students} selectedStudent={selectedStudent} onStudentChange={handleStudentChange} />
  );

  return (
    <div className="app light-theme">
      <Router>
        <Routes>
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/dashboard" element={isAuthenticated && students.length > 0 && selectedStudent ? renderHomePage() : <Navigate to="/" />} />
          <Route path="/rank-details" element={isAuthenticated && students.length > 0 && selectedStudent ? renderRankDetails() : <Navigate to="/" />} />
          <Route path="/achievements" element={isAuthenticated && students.length > 0 && selectedStudent ? renderAchievements() : <Navigate to="/" />} />
          <Route path="/attendance" element={isAuthenticated && students.length > 0 && selectedStudent ? renderAttendance() : <Navigate to="/" />} />
          <Route path="/fees-details" element={isAuthenticated && students.length > 0 && selectedStudent ? renderFeesDetails() : <Navigate to="/" />} />
          <Route path="/profile" element={isAuthenticated && students.length > 0 && selectedStudent ? renderProfile() : <Navigate to="/" />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
